import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Trash2, FileText, FolderOpen, BookOpen, SortAsc,
  ExternalLink, Calendar, Users, Award, ChevronDown, ChevronUp, ScrollText,
} from 'lucide-react'
import { removeListItem, getJournal, getPatent } from '../../services/api'
import type { PersonalItem } from '../../types'
import EmptyState from '../shared/EmptyState'
import InlineConnectionsPanel from '../graph/InlineConnectionsPanel'
import { formatTopicLabel } from '../../utils/topics'
import { shortDate } from '../../lib/shortDate'

interface ListItemsProps {
  items: PersonalItem[]
  listId: string
}

const TYPE_ICONS: Record<string, React.ReactNode> = {
  document: <FileText size={14} className="text-iflow-500" />,
  project: <FolderOpen size={14} className="text-green-500" />,
  journal: <BookOpen size={14} className="text-purple-500" />,
  // BUG-104: patents saved to lists rendered as dead rows (no icon, no click)
  patent: <ScrollText size={14} className="text-amber-500" />,
}

/**
 * Real, always-visible per-item metadata line (Fig46) — e.g. "Article ·
 * Nature Energy · 2023 · 214 citations · DOI 10.1038/…" or "Patent · Samsung
 * SDI · US · 2023" — sourced from the same real getJournal()/getPatent()
 * records the expandable JournalCard and patent detail view already use (same
 * query keys, so no duplicate fetch when a journal row is later expanded).
 * Only the fields the record actually carries are shown; nothing invented.
 * Renders nothing for document/project rows (no comparable metadata exists)
 * or while the lookup is loading/unavailable — an honest omission, not a
 * fabricated line.
 */
function ItemInlineMeta({ item }: { item: PersonalItem }) {
  const journalQuery = useQuery({
    queryKey: ['journal', item.resource_id],
    queryFn: () => getJournal(item.resource_id),
    enabled: item.resource_type === 'journal',
  })
  const patentQuery = useQuery({
    queryKey: ['patent', item.resource_id],
    queryFn: () => getPatent(item.resource_id),
    enabled: item.resource_type === 'patent',
  })

  if (item.resource_type === 'journal') {
    if (journalQuery.isLoading) return <div className="h-3 w-40 bg-gray-100 rounded animate-pulse mt-1" />
    const j = journalQuery.data
    if (!j) return null
    const parts: string[] = []
    if (j.journal_name) parts.push(j.journal_name)
    if (j.publication_date) parts.push(j.publication_date.slice(0, 4))
    if (j.citation_count != null && j.citation_count > 0) parts.push(`${j.citation_count.toLocaleString()} citations`)
    if (parts.length === 0 && !j.doi) return null
    return (
      <p className="text-xs text-gray-500 truncate">
        Article{parts.length > 0 && <> · {parts.join(' · ')}</>}
        {j.doi && <> · DOI: {j.doi}</>}
      </p>
    )
  }

  if (item.resource_type === 'patent') {
    if (patentQuery.isLoading) return <div className="h-3 w-40 bg-gray-100 rounded animate-pulse mt-1" />
    const p = patentQuery.data
    if (!p) return null
    const parts: string[] = []
    if (p.assignee) parts.push(p.assignee)
    if (p.jurisdiction) parts.push(p.jurisdiction)
    const year = (p.publication_date || p.filing_date)?.slice(0, 4)
    if (year) parts.push(year)
    if (parts.length === 0) return null
    return <p className="text-xs text-gray-500 truncate">Patent · {parts.join(' · ')}</p>
  }

  return null
}

/** Inline detail card for a journal article — fetches metadata on expand */
function JournalCard({ resourceId }: { resourceId: string }) {
  const { data: journal, isLoading } = useQuery({
    queryKey: ['journal', resourceId],
    queryFn: () => getJournal(resourceId),
  })

  if (isLoading) {
    return (
      <div className="px-4 py-3 animate-pulse">
        <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
      </div>
    )
  }

  if (!journal) return <p className="px-4 py-2 text-xs text-gray-500">Could not load journal details.</p>

  return (
    <div className="px-4 py-3 space-y-2">
      {/* Authors */}
      {journal.authors && journal.authors.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users size={13} className="text-gray-500 flex-shrink-0" />
          <span className="line-clamp-2">{journal.authors.join(', ')}</span>
        </div>
      )}

      {/* Publication info row */}
      <div className="flex flex-wrap items-center gap-3">
        {journal.journal_name && (
          <span className="text-sm text-gray-600 italic">{journal.journal_name}</span>
        )}
        {journal.publication_date && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Calendar size={12} /> {journal.publication_date}
          </span>
        )}
        {journal.citation_count != null && journal.citation_count > 0 && (
          <span className="flex items-center gap-1 text-xs text-gray-500">
            <Award size={12} /> {journal.citation_count.toLocaleString()} citations
          </span>
        )}
      </div>

      {/* Badges */}
      <div className="flex flex-wrap items-center gap-1.5">
        {journal.peer_reviewed && (
          <span className="px-2 py-0.5 text-[10px] font-medium bg-green-50 text-green-700 rounded-full">Peer Reviewed</span>
        )}
        {journal.open_access && (
          <span className="px-2 py-0.5 text-[10px] font-medium bg-iflow-50 text-iflow-700 rounded-full">Open Access</span>
        )}
        {journal.resource_type && (
          <span className="px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded-full">{journal.resource_type}</span>
        )}
      </div>

      {/* DOI */}
      {journal.doi && (
        <a
          href={`https://doi.org/${journal.doi}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-iflow-600 hover:text-iflow-700"
        >
          <ExternalLink size={12} /> DOI: {journal.doi}
        </a>
      )}

      {/* Abstract */}
      {journal.abstract && (
        <div className="pt-2 border-t border-gray-100">
          <p className="text-xs text-gray-600 leading-relaxed line-clamp-4">{journal.abstract}</p>
        </div>
      )}

      {/* Topics */}
      {journal.topics && journal.topics.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1">
          {journal.topics.slice(0, 8).map((t) => (
            <span key={t} className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded-full">{formatTopicLabel(t)}</span>
          ))}
          {journal.topics.length > 8 && (
            <span className="text-[10px] text-gray-500">+{journal.topics.length - 8} more</span>
          )}
        </div>
      )}
    </div>
  )
}

export default function ListItems({ items, listId }: ListItemsProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [sortBy, setSortBy] = useState<'added' | 'title'>('added')
  const [filterText, setFilterText] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const removeMutation = useMutation({
    mutationFn: (itemId: string) => removeListItem(listId, itemId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['list-items', listId] }),
  })

  const sorted = [...items]
    .filter((i) => i.title.toLowerCase().includes(filterText.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'title') return a.title.localeCompare(b.title)
      return new Date(b.added_at).getTime() - new Date(a.added_at).getTime()
    })

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === sorted.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(sorted.map((i) => i.resource_id)))
    }
  }

  const handleItemClick = (item: PersonalItem) => {
    if (item.resource_type === 'document') {
      navigate(`/document/${item.resource_id}`)
    } else if (item.resource_type === 'journal') {
      setExpandedId((prev) => (prev === item.resource_id ? null : item.resource_id))
    } else if (item.resource_type === 'project') {
      navigate(`/project/${item.resource_id}`)
    } else if (item.resource_type === 'patent') {
      // BUG-104: patents had no branch here, making saved patents dead rows.
      // The Patents page deep-links a detail view from /patents/{id}.
      navigate(`/patents/${item.resource_id}`)
    }
  }

  // The first selected item drives the connections panel
  const connectionsResourceId = selectedIds.size > 0 ? Array.from(selectedIds)[0] : null

  if (!items.length) {
    return (
      <EmptyState
        icon={<FileText size={24} />}
        title="Empty list"
        description="Add items from search results or document views."
      />
    )
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <input
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder="Filter items..."
          className="flex-1 px-3 py-1.5 light-input text-sm outline-none"
        />
        <button
          onClick={() => setSortBy(sortBy === 'added' ? 'title' : 'added')}
          className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <SortAsc size={12} /> {sortBy === 'added' ? 'Date added' : 'Title'}
        </button>
      </div>

      {/* Select all / selection bar */}
      <div className="flex items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
          <input
            type="checkbox"
            checked={sorted.length > 0 && selectedIds.size === sorted.length}
            onChange={toggleSelectAll}
            className="w-3.5 h-3.5 text-iflow-500 border-gray-300 rounded focus:ring-iflow-500"
          />
          {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
        </label>
        {selectedIds.size > 0 && (
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-gray-500 hover:text-gray-600"
          >
            Clear
          </button>
        )}
      </div>

      <div className="space-y-2">
        {sorted.map((item) => {
          const isExpanded = expandedId === item.resource_id
          const isSelected = selectedIds.has(item.resource_id)

          return (
            <div
              key={item.resource_id}
              className={`bg-white border rounded-lg transition-colors ${
                isSelected ? 'border-iflow-300 ring-1 ring-iflow-200' : 'border-gray-200 hover:border-iflow-300'
              }`}
            >
              <div className="flex items-center gap-3 p-3">
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleSelect(item.resource_id)}
                  className="w-4 h-4 text-iflow-500 border-gray-300 rounded focus:ring-iflow-500 flex-shrink-0"
                />

                {/* Type icon */}
                <div className="flex-shrink-0">{TYPE_ICONS[item.resource_type] || <FileText size={14} />}</div>

                {/* Title — clickable */}
                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => handleItemClick(item)}
                >
                  <p className="text-sm font-medium text-gray-900 truncate hover:text-iflow-600 transition-colors">
                    {item.title}
                  </p>
                  {item.notes && <p className="text-xs text-gray-500 truncate">{item.notes}</p>}
                  <ItemInlineMeta item={item} />
                </div>

                {/* Expand/collapse for journals */}
                {item.resource_type === 'journal' && (
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : item.resource_id)}
                    className="p-1 text-gray-500 hover:text-gray-600 flex-shrink-0"
                    title={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                )}

                {/* Date */}
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {shortDate(item.added_at)}
                </span>

                {/* Remove */}
                <button
                  onClick={() => removeMutation.mutate(item.resource_id)}
                  className="p-1 text-gray-500 hover:text-red-600 flex-shrink-0"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {/* Inline journal detail card */}
              {item.resource_type === 'journal' && isExpanded && (
                <div data-testid="journal-detail-card" className="border-t border-gray-100 bg-gray-50/50">
                  <JournalCard resourceId={item.resource_id} />
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Connections panel — shows when items are selected */}
      {connectionsResourceId && (
        <div className="mt-6">
          <InlineConnectionsPanel
            resourceId={connectionsResourceId}
            title={selectedIds.size === 1 ? 'Connections' : `Connections (${selectedIds.size} selected)`}
          />
        </div>
      )}
    </div>
  )
}
